<template>
  <v-dialog v-model="openDialog" scrollable width="800px">
    <v-card>
      <v-card-title>
        <p class="section-title" data-cy="modalTitle">{{ cardTitle }}</p>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6" class="pb-0 pt-1">
            <v-text-field v-model="contact.name" label="Nome" :error-messages="errorMessage.name" outlined dense data-cy="contactNameModal" />
          </v-col>
          <v-col cols="6" class="pb-0 pt-1">
            <v-text-field
              v-model="contact.documentNumber"
              v-mask="['###.###.###-##']"
              label="CPF"
              :error-messages="errorMessage.documentNumber"
              dense
              outlined
              data-cy="contactDocumentNoModal"
            />
          </v-col>
          <v-col cols="6" class="py-0">
            <v-text-field
              v-model="contact.phone"
              v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
              label="Telefone"
              :error-messages="errorMessage.phone"
              outlined
              dense
              data-cy="contactPhoneModal"
            />
          </v-col>
          <v-col cols="6" class="py-0">
            <v-text-field
              v-model="contact.phoneSecondary"
              v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
              label="Telefone Secundário"
              :error-messages="errorMessage.phoneSecondary"
              dense
              outlined
              data-cy="contactSecondaryPhoneModal"
            />
          </v-col>
          <v-col cols="6" class="py-0">
            <v-text-field v-model="contact.email" label="E-mail" :error-messages="errorMessage.email" outlined dense data-cy="contactEmailModal" />
          </v-col>
          <v-col cols="6" class="py-0">
            <v-text-field
              v-model="contact.birthDate"
              v-mask="'##/##/####'"
              label="Data de nascimento"
              :error-messages="errorMessage.birthDate"
              dense
              outlined
              data-cy="contactBirthDateModal"
            />
          </v-col>
        </v-row>
        <mf-action-buttons
          :primary-button="{
            text: 'Confirmar',
            action: validateFields,
            isVisible: true,
            isLoading: loading,
            dataCy: 'confirmChangesModal',
          }"
          :extra-button="{
            text: 'Cancelar',
            action: () => clearAndCloseModal(false),
            isVisible: true,
            dataCy: 'cancelChangesModal',
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { required, minLength, email } from 'vuelidate/lib/validators'
import { validBirthdate } from '@/helpers/validators.js'
import { MUTATION_CREATE_EXTRA_NUMBER, MUTATION_EDIT_EXTRA_NUMBER } from '@/modules/configuracoes/gerais/graphql'

const hasTwoWords = (value) => value && value.split(' ').filter((word) => word.length).length >= 2

export default {
  name: 'ContactsManagementModal',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    editContact: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    errorMessage: {
      name: '',
      documentNumber: '',
      phone: '',
      phoneSecondary: '',
      email: '',
      birthDate: '',
    },
    errors: {
      name: {
        required: 'Campo obrigatório.',
        minLength: 'O título possui menos de 3 caracteres.',
        hasTwoWords: 'Deve ter pelo menos 2 nomes.',
      },
      documentNumber: {
        required: 'Campo obrigatório.',
        minLength: 'O documento possui menos de 11 caracteres.',
      },
      phone: {
        required: 'Campo obrigatório.',
        minLength: 'O número possui menos de 11 caracteres.',
      },
      phoneSecondary: {
        minLength: 'O número possui menos de 11 caracteres.',
      },
      email: {
        required: 'Campo obrigatorio.',
        email: 'E-mail inválido',
      },
      birthDate: {
        required: 'Campo obrigatorio.',
        minLength: 'O número possui menos de 10 caracteres.',
        valid: 'Data de nascimento inválida.',
      },
    },
    contact: {
      name: '',
      documentNumber: '',
      phone: '',
      phoneSecondary: '',
      email: '',
      birthDate: '',
    },
    validationArray: ['name', 'documentNumber', 'phone', 'phoneSecondary', 'email', 'birthDate'],
  }),
  computed: {
    cardTitle() {
      return this.editContact._id ? 'Editar Contato' : 'Criar Contato'
    },
    openDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },
  created() {
    if (this.editContact.hasOwnProperty('_id') && this.value) {
      this.contact = this.editContact
      this.$v.$touch()
      return
    }
    this.contact = {}
  },
  methods: {
    clearAndCloseModal(hasToRefresh) {
      if (hasToRefresh) {
        this.$emit('update-data')
      }
      this.contact = {
        name: '',
        documentNumber: '',
        phone: '',
        phoneSecondary: '',
        email: '',
        birthDate: '',
      }
      this.openDialog = false
    },
    validateFields() {
      this.$v.$touch()

      this.validationArray.forEach((validation) => {
        if (!this.$v.contact[validation].required && this.$v.contact[validation].$invalid && validation !== 'phoneSecondary') {
          this.errorMessage[validation] = this.errors[validation].required
          return
        }
        if (!this.$v.contact[validation].minLength && validation !== 'email') {
          this.errorMessage[validation] = this.errors[validation].minLength
          return
        }
        if (validation === 'name' && !this.$v.contact[validation].hasTwoWords) {
          this.errorMessage[validation] = this.errors[validation].hasTwoWords
          return
        }
        if (validation === 'email' && !this.$v.contact[validation].email) {
          this.errorMessage[validation] = this.errors[validation].email
          return
        }
        if (validation === 'birthDate' && !this.$v.contact[validation].valid) {
          this.errorMessage[validation] = this.errors[validation].valid
          return
        }
        this.errorMessage[validation] = ''
      })
      if (this.$v.$error) {
        return
      }

      this.saveContact()
    },
    async saveContact() {
      try {
        this.loading = true

        const { _id, name, documentNumber, phone, phoneSecondary, email, birthDate } = this.contact
        const useMutation = _id ? MUTATION_EDIT_EXTRA_NUMBER : MUTATION_CREATE_EXTRA_NUMBER
        const newContact = {
          name,
          document_no: documentNumber,
          phone,
          phone_secondary: phoneSecondary,
          email,
          birth_date: birthDate.split('/').reverse().join('-'),
        }

        if (_id) {
          newContact._id = _id
        }

        await this.$apollo.mutate({
          mutation: useMutation,
          variables: {
            data: newContact,
          },
        })

        this.$snackbar({ message: 'Contato salvo com sucesso' })
        this.loading = false
        this.clearAndCloseModal(true)
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao salvar o contato' })
        this.loading = false
      }
    },
  },
  validations() {
    return {
      contact: {
        name: {
          required,
          minLength: minLength(3),
          hasTwoWords,
        },
        documentNumber: {
          required,
          minLength: minLength(14),
        },
        phone: {
          required,
          minLength: minLength(18),
        },
        phoneSecondary: {
          minLength: minLength(18),
        },
        email: {
          required,
          email,
        },
        birthDate: {
          required,
          minLength: minLength(10),
          valid: validBirthdate,
        },
      },
    }
  },
}
</script>
